import clsx from "clsx";
import { useRef } from "react";

import SearchInput from "common/components/SearchInput";
import ProfileImage from "common/components/ui/ProfileImage";
import type { LinkedInProfile } from "common/types";

import type { BlacklistType, Company } from "../../../types";

export default function AddToBlacklistSearch<
  Original extends LinkedInProfile | Company,
  T extends {
    id: string;
    pictureUrl: string;
    title: string;
    subtitle: string;
    originalObject: Original;
  },
>({
  searchResults,
  onSelectResult,
  searchText,
  setSearchText,
  isLoading,
  type,
}: {
  searchResults: T[] | undefined;
  onSelectResult: (result: T["originalObject"]) => void;
  searchText: string;
  setSearchText: (searchText: string) => void;
  isLoading: boolean;
  type: BlacklistType;
}) {
  const inputRef = useRef<HTMLInputElement>();

  function addBlacklistItem(blacklistItem: Original) {
    onSelectResult(blacklistItem);
    setSearchText("");
    inputRef.current.focus();
  }

  return (
    <div className="relative mb-4 mx-6 lg:mb-4 lg:mr-auto">
      <SearchInput
        ref={inputRef}
        placeholder={`Search ${type}`}
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        onClear={() => setSearchText("")}
        isLoading={isLoading}
        data-cy="blacklist-search"
        className="lg:w-80"
      />
      <div
        className={clsx(
          "absolute top-full mt-1 flex h-60 w-full flex-col gap-y-1 overflow-auto rounded-3xl bg-white py-2 shadow-md transition-opacity",
          searchText && !!searchResults && !isLoading
            ? "opacity-100"
            : "pointer-events-none opacity-0",
        )}
        data-cy="blacklist-search-results-wrapper"
      >
        {searchResults?.length ? (
          searchResults.map((blacklistItem) => {
            const { id, pictureUrl, title, subtitle, originalObject } =
              blacklistItem;
            return (
              <button
                className="flex gap-x-2 px-4 py-2 hover:bg-black-50"
                type="button"
                key={id}
                onClick={() => addBlacklistItem(originalObject)}
              >
                <ProfileImage src={pictureUrl} />
                <div className="flex flex-col gap-y-0.5">
                  <span className="line-clamp-1 text-left text-button-sm">
                    {title}
                  </span>
                  <span className="line-clamp-1 text-left text-xs text-black-400">
                    {subtitle}
                  </span>
                </div>
              </button>
            );
          })
        ) : (
          <p className="text-black-500 m-auto text-center">No results found</p>
        )}
      </div>
    </div>
  );
}
